import { useState, useEffect } from "react";
import { Button, Table, Modal, Form } from "react-bootstrap";
import { FaTrash, FaSearch, FaFileAlt, FaPlus } from "react-icons/fa";
import { toast } from "react-toastify";
import DashboardLayout from "../components/layout/DashboardLayout";
import ReactPaginate from "react-paginate";
import { PropagateLoader } from "react-spinners";
import { Dropdown } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { FaEye } from "react-icons/fa";
import { useTranslate } from "../hooks/LanguageProvider";
import DocumentPreviewModal, {
  PreviewableDocument,
} from "../components/common/DocumentPreviewModal";
import { buildPreviewableDocument } from "../utilities/uploadFiles";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

interface Document {
  id_document: number;
  document_type: string;
  file_path: string;
  linked_product?: string;
  document_status?: string;
  is_required?: number;
  industrial_usage?: string;
  ministry_comment?: string;
}

export default function SupplierDocuments() {
  const { translate } = useTranslate();
const t = translate;
  const [searchParams] = useSearchParams();
  const id_user =
    searchParams.get("id_user") ?? localStorage.getItem("userid") ?? "";

  const [documents, setDocuments] = useState<Document[]>([]);
  const [search, setSearch] = useState("");
  const [column, setColumn] = useState("document_type");
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState("ASC");
  const [colum, setColum] = useState("document_type");
  const [loading, setLoading] = useState(false);

  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [previewFile, setPreviewFile] = useState<PreviewableDocument | null>(
    null,
  );

  const [showModal, setShowModal] = useState(false);
  const [documentType, setDocumentType] = useState("");
  const [linkedProduct, setLinkedProduct] = useState("");
  const [documentStatus, setDocumentStatus] = useState("");
  const [isRequired, setIsRequired] = useState("0");
  const [industrialUsage, setIndustrialUsage] = useState("");
  const [ministryComment, setMinistryComment] = useState("");
  const [file, setFile] = useState<File | null>(null);

  const displayValue = (value: any) =>
    value === null || value === undefined || value === "" ? "Non renseigné" : value;

  const formatYesNo = (value: any) => Number(value) === 1 ? "Oui" : "Non";

  /* ================= LOAD ================= */

  const loadDocuments = async () => {
    setLoading(true);

    const res = await fetch(`${backendUrl}/api/supplier-documents/${id_user}`);
    const data = await res.json();

    setDocuments(data);

    setTimeout(() => setLoading(false), 400);
  };

  useEffect(() => {
    loadDocuments();
  }, []);

  /* ================= FILTER ================= */

  const filtered = documents.filter((doc) =>
    doc[column as keyof Document]
      ?.toString()
      .toLowerCase()
      .includes(search.toLowerCase()),
  );

  /* ================= PAGINATION ================= */

  const offset = page * limit;
  const current = filtered.slice(offset, offset + limit);
  const pageCount = Math.ceil(filtered.length / limit);

  /* ================= SORT ================= */

  const handleSortingColum = (col: string) => {
    setLoading(true);

    const newSort = sort === "ASC" ? "DESC" : "ASC";
    setSort(newSort);
    setColum(col);

    const sorted = [...documents].sort((a: any, b: any) => {
      if (a[col] < b[col]) return newSort === "ASC" ? -1 : 1;
      if (a[col] > b[col]) return newSort === "ASC" ? 1 : -1;
      return 0;
    });

    setDocuments(sorted);

    setTimeout(() => setLoading(false), 300);
  };

  /* ================= SELECT ================= */

  const handleSelectRow = (id: number) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    if (selectedRows.length === current.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(current.map((d) => d.id_document));
    }
  };

  /* ================= DELETE ================= */

  const deleteDocument = async (id: number) => {
    await fetch(`${backendUrl}/api/supplier-documents/delete/${id}`, {
      method: "DELETE",
    });
    toast.success(t("document_deleted"));
    loadDocuments();
  };

  /* ================= UPLOAD ================= */

  const uploadDocument = async () => {
    if (!file) return toast.error(t("select_file"));

    const formData = new FormData();
    formData.append("file", file);
    formData.append("id_user", id_user);
    formData.append("document_type", documentType);
    formData.append("linked_product", linkedProduct);
    formData.append("document_status", documentStatus);
    formData.append("is_required", isRequired);
    formData.append("industrial_usage", industrialUsage);
    formData.append("ministry_comment", ministryComment);

    await fetch(`${backendUrl}/api/supplier-documents/upload`, {
      method: "POST",
      body: formData,
    });

    toast.success(t("document_uploaded"));
    setShowModal(false);
    setDocumentType("");
    setLinkedProduct("");
    setDocumentStatus("");
    setIsRequired("0");
    setIndustrialUsage("");
    setMinistryComment("");
    setFile(null);
    loadDocuments();
  };

  const openDocumentPreview = (doc: Document) => {
    const preview = buildPreviewableDocument(
      doc.file_path,
      doc.document_type,
      "documents",
      t("document"),
    );

    if (preview) setPreviewFile(preview);
  };

  return (
    <div className="container-fluid">
      {/* HEADER */}
      <div className="d-flex justify-content-between mb-3">
        <h4>
          <FaFileAlt /> {t("documents")} ({documents.length})
        </h4>

        <div className="d-flex gap-2">
          <Button variant="warning" onClick={() => setShowModal(true)}>
            <FaPlus /> {t("add_document")}
          </Button>
        </div>
      </div>
      <div className="d-flex justify-content-between mb-3">
        {/* SEARCH */}
        <div className="col-md-4">
          <div className="input-group">
            <Dropdown>
              <Dropdown.Toggle variant="link">
                <i className="fas fa-chevron-down"></i>
              </Dropdown.Toggle>

              <Dropdown.Menu>
                <Dropdown.Item onClick={() => setColumn("document_type")}>
                  {t("type")}
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setColumn("linked_product")}>
                  Produit lié
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setColumn("document_status")}>
                  Statut document
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setColumn("industrial_usage")}>
                  Usage industriel
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            <input
              type="text"
              className="form-control"
              placeholder={`${t("search_by")} ${t(column)}`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <Button variant="secondary" onClick={() => setSearch("")}>
              ✕
            </Button>
          </div>
        </div>

        {/* AFFICHER */}
        <div className="col-md-8 d-flex justify-content-end align-items-center">
          <div className="dataTables_length d-flex align-items-center">
            <label style={{ marginBottom: 0 }}>
              {t("show")}
              <select
                className="custom-select custom-select-sm form-control form-control-sm ms-2"
                style={{ width: "70px" }}
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(0);
                }}
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
                <option value="500">500</option>
              </select>
            </label>
          </div>
        </div>
      </div>

      {/* TABLE */}
      <Table className="dataTable role-table">
        <thead>
          <tr className="ligth ligth-data">
            <th>
              <div className="checkbox d-inline-block">
                <input
                  type="checkbox"
                  className="checkbox-input"
                  checked={
                    selectedRows.length === current.length && current.length > 0
                  }
                  onChange={handleSelectAll}
                />
              </div>
            </th>

            <th
              className="sorting"
              onClick={() => handleSortingColum("document_type")}
            >
              {t("type")}
            </th>
            <th
              className="sorting"
              onClick={() => handleSortingColum("file_path")}
            >
              {t("file")}
            </th>

            <th>Produit lié</th>
            <th>Statut document</th>
            <th>Obligatoire</th>
            <th>Usage industriel</th>
            <th>Commentaire</th>
            <th>{t("actions")}</th>
          </tr>
        </thead>

       <tbody className="ligth-body">
  {loading ? (
    <tr>
      <td colSpan={9} className="text-center">
        <PropagateLoader color="#123abc" size={10} />
      </td>
    </tr>
  ) : current.length > 0 ? (
    current.map((doc) => (
      <tr key={doc.id_document}>
        <td>
          <div className="checkbox d-inline-block">
            <input
              type="checkbox"
              className="checkbox-input"
              checked={selectedRows.includes(doc.id_document)}
              onChange={() => handleSelectRow(doc.id_document)}
            />
          </div>
        </td>

        <td>{doc.document_type}</td>

        <td>
          {!doc.file_path ? (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "6px 16px",
                borderRadius: "999px",
                fontSize: "13px",
                fontWeight: 600,
                background: "#f3f4f6",
                color: "#9ca3af",
                border: "1px solid #e5e7eb",
              }}
            >
              {t("no_file")}
            </span>
          ) : (
            <button
              type="button"
              onClick={() => openDocumentPreview(doc)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                padding: "6px 16px",
                borderRadius: "999px",
                fontSize: "13px",
                fontWeight: 600,
                textDecoration: "none",
                background: "#fff7ed",
                color: "#d97706",
                border: "1px solid #fbbf24",
                transition: "all 0.2s ease",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#ffedd5";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#fff7ed";
              }}
            >
              <FaEye size={12} /> {t("view")}
            </button>
          )}
        </td>

        <td>{displayValue(doc.linked_product)}</td>
        <td>{displayValue(doc.document_status)}</td>
        <td>{formatYesNo(doc.is_required)}</td>
        <td>{displayValue(doc.industrial_usage)}</td>
        <td>{displayValue(doc.ministry_comment)}</td>

        <td>
          <Button
            size="sm"
            variant="danger"
            onClick={() => deleteDocument(doc.id_document)}
            title={t("delete")}
          >
            <FaTrash />
          </Button>
        </td>
      </tr>
    ))
  ) : (
    <tr>
      <td colSpan={9} style={{ textAlign: "center" }}>
        {t("no_documents")}
      </td>
    </tr>
  )}
</tbody>
      </Table>

      {/* PAGINATION */}
      <div className="d-flex justify-content-between">
        <span>
          {t("displaying")} {current.length} {t("out_of")} {filtered.length}
        </span>
        <ReactPaginate
          previousLabel={t("previous")}
          nextLabel={t("next")}
          breakLabel={"..."}
          pageCount={pageCount}
          marginPagesDisplayed={2}
          pageRangeDisplayed={3}
          onPageChange={(d) => setPage(d.selected)}
          containerClassName={"pagination justify-content-end"}
          pageClassName={"page-item"}
          pageLinkClassName={"page-link"}
          previousClassName={"page-item"}
          previousLinkClassName={"page-link"}
          nextClassName={"page-item"}
          nextLinkClassName={"page-link"}
          activeClassName={"active"}
          forcePage={page}
        />
      </div>

      {/* MODAL */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t("upload_document")}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
             <Form.Label>{t("type")}</Form.Label>
              <Form.Control
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Produit lié</Form.Label>
              <Form.Control
                value={linkedProduct}
                onChange={(e) => setLinkedProduct(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Statut document</Form.Label>
              <Form.Select
                value={documentStatus}
                onChange={(e) => setDocumentStatus(e.target.value)}
              >
                <option value="">Non renseigné</option>
                <option value="Validé">Validé</option>
                <option value="Manquant">Manquant</option>
                <option value="À corriger">À corriger</option>
                <option value="Expiré">Expiré</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Obligatoire</Form.Label>
              <Form.Select value={isRequired} onChange={(e) => setIsRequired(e.target.value)}>
                <option value="0">Non</option>
                <option value="1">Oui</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Usage industriel</Form.Label>
              <Form.Select
                value={industrialUsage}
                onChange={(e) => setIndustrialUsage(e.target.value)}
              >
                <option value="">Non renseigné</option>
                <option value="Registre de commerce">Registre de commerce</option>
                <option value="Fiche technique produit">Fiche technique produit</option>
                <option value="Certificat qualité">Certificat qualité</option>
                <option value="Dossier homologation">Dossier homologation</option>
                <option value="Fiche capacité production">Fiche capacité production</option>
                <option value="Dossier export">Dossier export</option>
                <option value="Dossier investissement">Dossier investissement</option>
                <option value="Document SAV / maintenance">Document SAV / maintenance</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Commentaire ministère</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={ministryComment}
                onChange={(e) => setMinistryComment(e.target.value)}
              />
            </Form.Group>

           <Form.Group className="mt-3">
  <input
    id="documentFileInput"
    type="file"
    style={{ display: "none" }}
    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) setFile(e.target.files[0]);
    }}
  />

  <div
    onClick={() => document.getElementById("documentFileInput")?.click()}
    style={{
      border: "1px solid #ced4da",
      borderRadius: "8px",
      padding: "0",
      display: "flex",
      alignItems: "stretch",
      overflow: "hidden",
      cursor: "pointer",
      background: "#fff",
      minHeight: "42px",
    }}
  >
    <div
      style={{
        background: "#f8f9fa",
        borderRight: "1px solid #dee2e6",
        padding: "8px 14px",
        fontWeight: 500,
        whiteSpace: "nowrap",
        display: "flex",
        alignItems: "center",
      }}
    >
      {t("choose_file")}
    </div>

    <div
      style={{
        color: "#6c757d",
        padding: "8px 14px",
        flex: 1,
        display: "flex",
        alignItems: "center",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {file ? file.name : t("no_file_selected")}
    </div>
  </div>
</Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button onClick={uploadDocument}>{t("upload")}</Button>
        </Modal.Footer>
      </Modal>

      <DocumentPreviewModal
        document={previewFile}
        fallbackTitle={t("document")}
        onHide={() => setPreviewFile(null)}
        t={t}
      />
    </div>
  );
}
